<template>
  <div
    :id="id"
    class="comment"
    :style="styles"
    :class="(classObject, { notification: !isRead })"
  >
    <VDropdown
      ref="pop"
      popper-class="v-popper--comment-dropdown thread"
      placement="right-start"
      :delay="delayOptions"
      distance="3"
      container=".main"
      :boundary="boundary"
      :prevent-overflow="true"
      :overflow-padding="40"
      :shift="true"
      auto-size="min"
      :shown="isOpen"
      :triggers="[]"
      :auto-hide="autoHide"
      @hide="onHide"
      @auto-hide="onAutoHide"
      @apply-show="onApplyShow"
      @apply-hide="onApplyHide"
    >
      <div class="tooltip-target b3">
        <div
          class="wrapper"
          @contextmenu.prevent="onContextMenu"
          @mousedown="onMouseDown"
          @mouseup="onMouseUp"
        >
          <svg-icon
            v-if="isRead && !comment?.resolved"
            :width="'26'"
            :height="'25'"
            icon="app-icons/comment"
          />
          <svg-icon
            v-else-if="!isRead && !comment?.resolved"
            :width="'26'"
            :height="'25'"
            icon="app-icons/comment-notification"
            :svg-fill="true"
          />
          <svg-icon
            v-else
            :width="'26'"
            :height="'25'"
            icon="app-icons/comment-resolved"
          />
        </div>
      </div>
      <template #popper>
        <board-comment-thread :id="id" />
      </template>
    </VDropdown>
  </div>
</template>
<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import _ from 'lodash';
import jspSingleton from '@/lib/jspSingleton.js';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS } from '@/constants.js';
import SvgIcon from '../../all/SvgIcon.vue';
import BoardCommentThread from './BoardCommentThread.vue';
import misc from '../../mixins/misc.js';
import updater from '../../../vuex/project/updater.js';

export default {
  components: {
    SvgIcon,
    BoardCommentThread,
  },

  mixins: [misc],

  props: {
    id: {
      type: String,
      default: null,
    },
    virtualSelected: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { hasPermission: canComment } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_COMMENT],
      roles: ['editor', 'viewer'],
    });

    return {
      canComment,
    };
  },

  data() {
    return {
      dragCounter: 0,
      delayOptions: { show: 300, hide: 100 },
      autoHide: true,
      boundary: document.querySelector('.main'),
    };
  },

  computed: {
    ...mapGetters('project/data/notes', { getNote: 'getNode' }),
    ...mapGetters('project/data/elements', { getElement: 'getNode' }),
    ...mapGetters('project/data/jumpers', { getJumper: 'getNode' }),
    ...mapGetters('project/data/branches', { getBranch: 'getNode' }),
    ...mapGetters('project/data/comments', { getComment: 'getNode' }),
    ...mapGetters('project/config/comments', ['getIsSelected', 'getRead']),
    ...mapGetters('project/config', ['getGrid']),
    ...mapGetters('project/config/comments', { getOpenComment: 'getOpen' }),
    ...mapGetters('application', [
      'getExactTransform',
      'getPanning',
      'getBoardItemDragging',
    ]),
    ...mapGetters('application/modals', ['getCommentPopover']),

    /**
     * Return the comment
     * @returns {Object}    The comment
     */
    comment() {
      return this.getComment(this.id);
    },

    /**
     * Return the css styles for positioning
     * @returns {Object}    The { left, top, transform } styles
     */
    styles() {
      const { x, y } = this.comment?.position || { x: 0, y: 0 };
      return {
        left: `${x}px`,
        top: `${y}px`,
        transform: `scale(${1 / this.scale})`,
      };
    },

    /**
     * Return whether the current comment is selected
     * @returns {Boolean}   Whether it is selected
     */
    isSelected() {
      return this.getIsSelected(this.id);
    },

    /**
     * Custom dynamic classes applied to the comment
     * @returns {Object}    An object with the various classes and their values
     */
    classObject() {
      return {
        'jtk-drag-selected': this.isSelected,
        'virtual-selected': this.virtualSelected,
      };
    },

    /**
     * Return whether the current thread has the comment modal open
     * @returns {Boolean}
     */
    isOpen() {
      // reference here: https://v3-migration.vuejs.org/breaking-changes/attribute-coercion.html
      return this.getOpenComment === this.id ? true : null;
    },

    /**
     * Get the scale of the exact transform
     * @returns {Number}
     */
    scale() {
      return this.getExactTransform.scale;
    },

    /**
     * Return whether the current thread has been read
     * @returns {Boolean}
     */
    isRead() {
      return (
        this.getRead(this.comment?.id) &&
        this.comment?.children.every(id => this.getRead(id))
      );
    },
  },

  watch: {
    isSelected(newVal, oldVal) {
      if (newVal) {
        jspSingleton.instance.addToDragSelection(this.$el); // Add it to jsplumb selection
      } else {
        jspSingleton.instance.removeFromDragSelection(this.$el);
      }
    },

    /**
     * Redraw the popover when pan-zooming
     */
    getExactTransform(newVal, oldVal) {
      this.$refs.pop.onResize();
    },

    /**
     * While panning update the popper and do not close the comment
     */
    getPanning(newVal, oldVal) {
      console.log('------- GetPanning getter called --------');
      if (newVal) {
        this.autoHide = false;
        this.$refs.pop.onResize();
      } else {
        // Argh! There was no cleaner solution at the moment
        setTimeout(() => {
          this.autoHide = true;
        }, 300);
      }
    },

    /**
     * The comment's position is changed, update the popper
     */
    'comment.position': function position(newVal, oldVal) {
      this.$refs.pop.onResize();
    },
  },

  mounted() {
    // A comment is always draggable by commenters
    if (this.canComment) {
      // Make comment draggable
      jspSingleton.instance.manage(this.$el);
    }
  },

  /**
   * Lifecycle hook, before destroy
   */
  beforeUnmount() {
    console.log('Comment pin is being unmounted');

    // Remove it from jsplumb's list of managed elements
    jspSingleton.instance.unmanage(this.$el, false);
    // Make sure to deselect before hiding
    // if (this.isSelected) {
    //   this.deselectComment({ id: this.id, apply: true });
    // }
    // jspSingleton.instance.remove(this.$el);
  },

  methods: {
    ...mapActions('project/config/comments', {
      selectComment: 'select',
      deselectComment: 'deselect',
      setRead: 'setRead',
      setOpenComment: 'setOpen',
    }),
    ...mapActions('project/data/comments', { updateComment: 'customUpdate' }),
    ...mapActions('project/data/notes', { updateNote: 'customUpdate' }),
    ...mapActions('project/data/elements', { updateElement: 'customUpdate' }),
    ...mapActions('project/data/jumpers', { updateJumper: 'customUpdate' }),
    ...mapActions('project/data/branches', { updateBranch: 'customUpdate' }),
    ...mapActions('project/data/comments', { deleteComment: 'customDelete' }),
    ...mapActions('project/config', ['deselectAll', 'deleteSelections']),
    ...mapMutations('application/modals', ['setCommentPopover']),
    ...mapActions('application/contextMenu', [
      'createItem',
      'setCalcPosition',
      'deleteMenu',
    ]),

    updatePopper() {
      if (this.$refs.pop?.popperInstance) {
        this.$refs.pop.popperInstance.update();
      }
      if (this.getPanning) {
        requestAnimationFrame(this.updatePopper);
      }
    },

    /**
     * Event listener for v-popover apply-show event
     */
    onApplyShow() {
      console.log('BoardCommentPin::v-popover apply-show event');
      this.setCommentPopover(this.id);
    },

    /**
     * Event listener for v-popover apply-hide event
     */
    onApplyHide() {
      console.log('BoardCommentPin::v-popover apply-hide event');
      if (!this.comment) return;
      // Mark all comments in this thread as read
      const unreadIds = this.comment.children.filter(id => !this.getRead(id));
      if (!this.getRead(this.id)) {
        unreadIds.push(this.id);
      }
      this.setRead({ ids: unreadIds, apply: true });
    },

    /**
     * Event listener for v-popover hide and auto-hide events
     */
    onHide() {
      console.log('onHide comment:', this.comment);
      if (this.getCommentPopover === this.id) {
        this.setCommentPopover(null);
      }
      // Guard against pending event after deletion
      if (!this.comment) return;
      if (this.isOpen) {
        this.delayOptions.show = 300;
        this.setOpenComment({ id: null, apply: true });
      }
      // In case there is no content self-destruct
      if (!this.comment.content) {
        this.deleteComment({ id: this.id, apply: true });
      }
    },

    onAutoHide() {
      console.log('onAutoHide comment:', this.comment);
      // Guard against pending event after deletion
      if (!this.comment) return;
      if (this.isOpen) {
        this.delayOptions.show = 300;
        this.setOpenComment({ id: null, apply: true });
      }
      // In case there is no content self-destruct
      if (!this.comment.content) {
        this.deleteComment({ id: this.id, apply: true });
      }
    },

    /**
     * Event listener - context menu event.
     */
    onContextMenu(event) {
      // Select the jumper
      if (!this.isSelected) {
        this.deselectAll({ apply: false });
        // Clear the selections from jsplumb
        jspSingleton.instance.clearDragSelection();
        this.selectComment({ id: this.id, apply: true });
      }
      // Show the context menu
      this.createContextMenu(event.clientX, event.clientY);
    },

    /**
     * Create a context menu for this comment pin
     * @param {Number} x  The x client coordinate
     * @param {Number} y  The y client coordinate
     */
    async createContextMenu(x, y) {
      this.createItem({
        name: 'Delete selection',
        icon: null,
        commands: {
          doAction: () => {
            this.deleteSelections({ apply: true });
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
        submenu: null,
      });

      this.setCalcPosition({
        calcPosition: () => {
          return { left: x, top: y };
        },
      });
    },

    onMouseDown(event) {
      // On middle-click do not start drag
      if (event.button === 1) {
        event.preventDefault();
      }
    },

    /**
     * Event listener - mouse up event on comment
     */
    onMouseUp(event) {
      console.log('onMouseUp on pin');
      const { button } = event;
      console.log('Button is:', button);

      const isDragged = jspSingleton.instance.hasClass(this.$el, 'jtk-drag');
      // Check if we are after a drag or after a single (left) click
      if (!isDragged && button === 0) {
        if (!this.$el.contains(document.activeElement)) {
          this.removeAllRanges();
        }
        // When Ctrl or Shift pressed toggle selection
        if (event.ctrlKey || event.shiftKey || event.metaKey) {
          if (!this.isSelected) {
            this.selectComment({ id: this.id, apply: true });
          } else {
            this.deselectComment({ id: this.id, apply: true });
          }
        } else if (!this.isSelected) {
          console.log('Selecting comment');
          // Clear any selections and select only this note
          this.deselectAll({ apply: false });
          // Clear the selections from jsplumb
          jspSingleton.instance.clearDragSelection();
          this.selectComment({ id: this.id, apply: false });
          // Open the menu
          this.delayOptions.show = 0;
          this.setOpenComment({ id: this.id, apply: true });
        } else {
          // Open the menu in this case also
          this.delayOptions.show = 0;
          this.setOpenComment({ id: this.id, apply: true });
        }
      }
    },

    /**
     * Note is being dragged (jsplumb event)
     */
    onDrag(params) {
      console.log('Comment::onDrag params:', params);
      if (this.$refs.pop?.popperInstance) {
        this.$refs.pop.popperInstance.update();
      }
      this.autoHide = false;
    },

    /**
     * Event listener - Note has completed dragging (jsplumb event)
     */
    onEndDrag(params) {
      console.log('BoardCommentPin::onEndDrag');

      // Argh! There was no cleaner solution at the moment
      setTimeout(() => {
        this.autoHide = true;
      }, 300);
    },
  },
};
</script>

<style scoped lang="scss">
.comment {
  z-index: 6;
  position: absolute;
  box-sizing: border-box;
  width: 22px;
  height: 20px;
  :deep(svg path.check) {
    fill: #242929 !important;
  }

  :deep(svg) {
    overflow: visible;
    path.background {
      fill: #fff;
    }
  }
  &.notification {
    // margin-top: -6px;
    :deep(svg path) {
      fill: #ffffff;
      &:first-child {
        fill: #db841e;
      }
    }
  }
  &.jtk-drag-selected :deep(svg),
  &.virtual-selected :deep(svg) {
    overflow: visible;
    path {
      // stroke: #db841e;
      // stroke-width: 2px;
      fill: #db841e;
    }
  }
}
</style>
<style lang="scss">
.v-popper__popper.v-popper--theme-dropdown.v-popper--comment-dropdown {
  max-height: calc(100% - 60px);
  overflow-x: hidden;
  overflow-y: auto;
  &::-webkit-scrollbar-track {
    background-color: transparent;
    border: none;
  }

  &::-webkit-scrollbar {
    width: 15px;
    background-color: #171919;
  }

  &::-webkit-scrollbar-thumb {
    width: 15px;
    // border-left: 5px solid rgba(0, 0, 0, 0);
    background-color: #242829;
    background-clip: padding-box;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #424a4c;
  }
  .v-popper__inner {
    margin-right: auto;
    overflow-x: hidden;
    overflow-y: auto;
    width: 340px;
    border-radius: 3px;
    color: #bcbcbc;
    background: #0d0e0e;
    border: 1px solid #242929;
    padding: 0;
    box-shadow: none;

    .thumb {
      text-align: center;
      color: #fff;
      line-height: 44px;
      font-size: 14px;
      letter-spacing: 0.1em;
      background: #364345;
      border-radius: 44px;
      width: 28px;
      height: 28px;
      cursor: default;
      overflow: hidden;
      box-shadow: 0 0 0 0 var(--user-color);
      transition: box-shadow 0.2s;
      &.iscollaborator {
        cursor: pointer;
        &:hover {
          box-shadow: 0 0 0 3px var(--user-color); /* All the borders by using the spread properties */
        }
      }

      img {
        width: 100%;
        z-index: 1;
        position: relative;
        object-fit: cover;
        object-position: center top;
      }
      .initials {
        width: 28px;
        height: 28px;
        line-height: 28px;
        color: #e0e0e0;
        font-size: 11px;
        font-weight: 600;
        border-radius: 50%;
        z-index: 0;
        letter-spacing: -0.1px;
        user-select: none;
      }
    }
  }

  .v-popper__arrow-container {
    display: none;
  }
}
</style>
